import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";

import { useSelector, useDispatch } from "react-redux";
import { Box, TextField, Stack, IconButton } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  AddLine,
  AddFill,
  RemoveIconLine,
  RemoveIconFill,
} from "../../../ImageRepo";
import {
  selectEditConnectionConnectionPairsLoaded,
  selectEditConnectionConnectionPairs,
  selectEditConnectionAllConnections,
  selectEditConnectionId,
  setEditConnectionSelectedPairs,
  selectSelectedConnection,
  setSharedCatalogSetupDetails,
} from "@/features/connections/integrationSlice";
import ShepTooltip from "@/components/GenericComponents/ShepTooltip";
import ShepSearchBar from "@/components/GenericComponents/ShepSearchBar";
import ShepTable from "@/components/GenericComponents/ShepTable";
import SharedCatalogs from "./SharedCatalogs/SharedCatalogs";
const pairStatusLabel = [
  "Paired",
  "Paired",
  "Paired",
  "Not paired",
  "Not paired",
];

const pairStatus = {
  unChanged: 0,
  modified: 1,
  new: 2,
  deleted: 3,
  notAdded: 4,
};
const pairAction = { delete: 0, add: 1 };

const getAction = (status) => {
  if (status === pairStatus.deleted || status === pairStatus.notAdded)
    return pairAction.add;
  return pairAction.delete;
};

const ClickableEditor = forwardRef((params, ref) => {
  const { value: initialValue, status, nameChange } = params;
  const [editable, setEditable] = useState(false);
  const [value, setValue] = useState(initialValue || "");
  const [error, setError] = useState(false);

  useImperativeHandle(ref, () => ({
    setEditable: (editable) => setEditable(editable),
  }));

  //Check to ensure the editor is in a correct state on change. But it is still valid for
  //a connection pair to have no name if it's of a status indicating its not being added.
  useEffect(() => {
    if (
      !value.trim() && // true if no name given
      [pairStatus.new, pairStatus.modified].includes(status)
    ) {
      setError(true);
    } else {
      setError(false);
    }
  }, [value, status]);

  //We keep the editor open if it is in error
  const handleBlur = () => {
    if (!error) {
      setEditable(false);
      nameChange({ target: { value } });
    }
  };

  const handleFocus = () => {
    setEditable(true);
  };

  return (
    <>
      {!editable && (
        <Stack style={{ width: "85%" }} direction={"row"}>
          <Box style={{ display: "flex" }}>{value}</Box>
          <Box style={{ marginLeft: "auto", display: "flex" }}>
            <EditIcon color="disabled" onClick={handleFocus} />
          </Box>
        </Stack>
      )}
      {editable && (
        <TextField
          variant="outlined"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onBlur={handleBlur}
          autoFocus
          error={error}
          helperText={error ? "Enter a pair name" : ""}
          sx={{
            width: 200,
          }}
        />
      )}
    </>
  );
});

const ConnectionPair = ({
  modifiedConnectionPairs,
  setModifiedConnectionPairs,
  targetConnectionIds,
  setTargetConnectionIds,
  setHasErrors,
  showErrors,
}) => {
  const dispatch = useDispatch();
  const [currentRows, setCurrentRows] = useState([]);
  const recordLoaded = useSelector(selectEditConnectionConnectionPairsLoaded);
  const currentConnectionId = useSelector(selectEditConnectionId);
  const allConnectionPairs = useSelector(selectEditConnectionConnectionPairs);
  const originalRows = useSelector(selectEditConnectionAllConnections);
  const [rows, setRows] = useState([]);
  const editorRefs = useRef({});
  const [openSharedCatalog, setOpenSharedCatalog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  /*  Check to look for errors whenever our current rows change, or upon a failed save
      If we have a failed save, open the relevant ClickableEditor(s).  */
  useEffect(() => {
    const unnamedModifiedConnectionPairs = currentRows.filter(
      (x) =>
        (x.status === pairStatus.modified || x.status === pairStatus.new) &&
        !x.connectionPairName
    );

    setHasErrors(unnamedModifiedConnectionPairs.length > 0);

    if (showErrors) {
      unnamedModifiedConnectionPairs.forEach((pair) => {
        editorRefs.current[pair.ConnectionId]?.setEditable(true);
      });
    }
  }, [showErrors, currentRows, setHasErrors]);

  const columns = [
    {
      field: "ConnectionName",
      headerName: "Name",
      width: 220,
    },
    {
      field: "DisplayName",
      headerName: "Display Name",
      width: 220,
    },
    {
      field: "typeLabel",
      headerName: "Type",
      width: 220,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => <>{pairStatusLabel[params.value]}</>,
    },
    {
      field: "connectionPairName",
      headerName: "Pair Name",
      width: 250,
      renderCell: (params) => (
        <ClickableEditor
          ref={(ref) => (editorRefs.current[params.row.ConnectionId] = ref)}
          value={params.value}
          status={params.row.status}
          nameChange={(e) => {
            onPairNameChanges(params.row, e);
          }}
        />
      ),
    },
    {
      field: "ConnectionId",
      headerName: "Action",
      width: 150,
      renderCell: (params) => (
        <ShepTooltip
          title={
            getAction(params.row.status) === pairAction.add
              ? "Add connection pair with this connection"
              : "Remove connection pair"
          }
        >
          <IconButton onClick={() => onActionClicked(params.row)}>
            <Box
              sx={{
                position: "relative",
                width: 22,
                height: 22,
                "&:hover .line": {
                  opacity: 0,
                },
                "&:hover .fill": {
                  opacity: 1,
                },
              }}
            >
              <Box
                className="line"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 1,
                  transition: "opacity 0.2s",
                }}
              >
                {getAction(params.row.status) === pairAction.add ? (
                  <AddLine width={22} height={22} />
                ) : (
                  <RemoveIconLine width={22} height={22} />
                )}
              </Box>
              <Box
                className="fill"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: "opacity 0.2s",
                }}
              >
                {getAction(params.row.status) === pairAction.add ? (
                  <AddFill width={22} height={22} />
                ) : (
                  <RemoveIconFill width={22} height={22} />
                )}
              </Box>
            </Box>
          </IconButton>
        </ShepTooltip>
      ),
    },
    {
      field: "pairedInfo",
      headerName: "Share Catalog",
      width: 250,
      renderCell: (params) => {
        const statusText = pairStatusLabel[params.row.status];
        if (statusText === "Paired") {
          return (
            <EditIcon
              color="disabled"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedRow(params.row);
                setOpenSharedCatalog(true);
              }}
            />
          );
        }
        return null;
      },
    },
  ];

  const refreshRows = (text) => {
    if (text) {
      setCurrentRows(
        rows.filter(
          (x) =>
            ((x) => x.ConnectionId !== currentConnectionId) &&
            ((x.ConnectionName &&
              x.ConnectionName.toLowerCase().indexOf(text.toLowerCase()) >
              -1) ||
              (x.typeLabel &&
                x.typeLabel.toLowerCase().indexOf(text.toLowerCase()) > -1) ||
              (x.DisplayName &&
                x.DisplayName.toLowerCase().indexOf(text.toLowerCase()) > -1))
        )
      );
    } else {
      setCurrentRows(
        rows.filter((x) => x.ConnectionId !== currentConnectionId)
      );
    }
  };
  const handleTextChange = (event, data) => {
    refreshRows(event.target.value);
  };
  const onActionClicked = ({ ConnectionId, status, connectionPairId }) => {
    let nextStatus = getNextStatus({
      status,
      isAnExistingConnectionPair: !!connectionPairId,
    });

    if (
      nextStatus === pairStatus.deleted ||
      nextStatus === pairStatus.notAdded
    ) {
      editorRefs.current[ConnectionId]?.setEditable(false);
    }

    if (nextStatus === pairStatus.unChanged || nextStatus === pairStatus.new) {
      editorRefs.current[ConnectionId]?.setEditable(true);
    }

    updateStatus(nextStatus, ConnectionId);
  };

  const getNextStatus = ({ status, isAnExistingConnectionPair }) => {
    if (isAnExistingConnectionPair) {
      if (status === pairStatus.deleted) return pairStatus.unChanged;
      if (status === pairStatus.unChanged || status === pairStatus.modified)
        return pairStatus.deleted;
    }

    if (status === pairStatus.notAdded) return pairStatus.new;
    if (status === pairStatus.new) return pairStatus.notAdded;

    return status;
  };

  const updateStatus = (nextstatus, id) => {
    const selectedRows = currentRows.filter((x) => x.ConnectionId === id);
    if (
      selectedRows &&
      selectedRows.length &&
      selectedRows[0].status !== nextstatus
    ) {
      const newRows = currentRows.map((x) => {
        if (x.ConnectionId === id) return { ...x, status: nextstatus };
        return { ...x };
      });
      setCurrentRows(newRows);
      setModifiedConnectionPairs(
        newRows.filter(
          (x) =>
            x.status !== pairStatus.unChanged &&
            x.status !== pairStatus.notAdded
        )
      );
    }
  };
  const currentConnection = useSelector(selectSelectedConnection);

  const onPairNameChanges = (row, e) => {
    //preserving the sharedCatalogInformation when changing the name of a connection pair
    //since the connection pair object being sent back to the backend otherwise will set the setting info to NULL
    if (row?.sharedCatalogInfo) {
      const parsedData = JSON.parse(row.sharedCatalogInfo);
      const settings = parsedData.SharedCatalogSettings;

      const catalogSharingSetupDetails = {
        PrimaryConnectionId: settings.PrimaryConnectionId,
        SecondaryConnectionId: settings.SecondaryConnectionId,
        SelectedCatalogs: settings.SelectedCatalogs,
        ConnectionId1: currentConnection.ConnectionId,
        ConnectionId2: row.ConnectionId,
      };

      dispatch(
        setSharedCatalogSetupDetails({
          catalogSharingSetupDetails: catalogSharingSetupDetails,
        })
      );
    }

    const id = row.ConnectionId;
    const status = row.status;
    let nextStatus = status;
    if (status === pairStatus.notAdded) nextStatus = pairStatus.new;
    if (status === pairStatus.unChanged) nextStatus = pairStatus.modified;
    const selectedRows = currentRows.filter((x) => x.ConnectionId === id);
    if (selectedRows && selectedRows.length) {
      const newRows = currentRows.map((x) => {
        if (x.ConnectionId === id)
          return {
            ...x,
            connectionPairName: e.target.value,
            status: nextStatus,
          };
        return { ...x };
      });

      setCurrentRows(newRows);
      setModifiedConnectionPairs(
        newRows.filter(
          (x) =>
            x.status !== pairStatus.unChanged &&
            x.status !== pairStatus.notAdded
        )
      );
    }
  };

  useEffect(() => {
    const selectedPairs = currentRows
      .filter(
        (x) =>
          x.status === pairStatus.modified ||
          x.status === pairStatus.new ||
          x.status === pairStatus.unChanged
      )
      .map((x) => {
        return {
          connectionId: x.ConnectionId,
          connectionPairId: x.ConnectionPairId,
          connectionPairName: x.ConnectionName + " | " + x.connectionPairName,
        };
      });
    dispatch(setEditConnectionSelectedPairs(selectedPairs));
  }, [currentRows]);

  useEffect(() => {
    if (currentConnectionId && rows) {
      if (allConnectionPairs) {
        const connectionPairs = allConnectionPairs.filter(
          (x) =>
            x.ConnectionId1 === currentConnectionId ||
            x.ConnectionId2 === currentConnectionId
        );
        if (connectionPairs && connectionPairs.length) {
          const ids = connectionPairs.map((pair) => {
            const otherConnectionId =
              pair.ConnectionId1 == currentConnectionId
                ? pair.ConnectionId2
                : pair.ConnectionId1;
            return {
              id: otherConnectionId,
              name: pair.ConnectionPairName,
              connectionPairId: pair.ConnectionPairId,
              sharedCatalogInfo: pair.JSONProperties,
            };
          });

          const selectedRows = originalRows
            .filter(
              (r) => r.ConnectionId !== currentConnectionId && !r.IsDisabled
            )
            .map((connection) => {
              const matchingPairs = ids.filter(
                (x) => x.id === connection.ConnectionId
              );
              if (matchingPairs && matchingPairs.length) {
                const pair = matchingPairs[0];
                return {
                  ...connection,
                  connectionPairName: pair.name,
                  connectionPairId: pair.connectionPairId,
                  status: pairStatus.unChanged,
                  sharedCatalogInfo: pair.sharedCatalogInfo,
                };
              }
              return { ...connection, status: pairStatus.notAdded };
            });
          mergeRows(selectedRows);
          setRows(selectedRows);
        } else {
          const filteredRows = originalRows
            .filter(
              (x) => x.ConnectionId !== currentConnectionId && !x.IsDisabled
            )
            .map((c) => {
              return { ...c, status: pairStatus.notAdded };
            });
          // Check if targetConnectionIds is not null
          if (targetConnectionIds) {
            filteredRows.forEach((row) => {
              if (targetConnectionIds.includes(row.ConnectionId)) {
                row.status = pairStatus.new;
                editorRefs.current[row.ConnectionId]?.setEditable(true);
              }
            });
            setTargetConnectionIds([]);
          }
          mergeRows(filteredRows);
          setRows(filteredRows);
        }
      }
    }
  }, [allConnectionPairs, currentConnectionId, originalRows]);

  const mergeRows = (filteredRows) => {
    if (modifiedConnectionPairs) {
      const mergedRows = [...modifiedConnectionPairs];
      filteredRows.forEach((row) => {
        if (!mergedRows.some((x) => x.ConnectionId === row.ConnectionId)) {
          mergedRows.push(row);
        }
      });
      setCurrentRows(mergedRows);
    } else {
      setCurrentRows(filteredRows);
    }
  };

  return (
    <>
      <Box sx={{ display: "block", flexDirection: "column" }}>
        <Stack direction="row" style={{ display: "flex", padding: "15px 0px" }}>
          <ShepSearchBar
            placeholder="Search by Names or Type"
            onChange={handleTextChange}
            sx={{
              width: "350px",
            }}
          />
        </Stack>
        <Box
          className="content-box"
          sx={{ display: "flex", flexDirection: "row", padding: "0px" }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                borderRadius: 5,
                backgroundColor: "white",
                marginBottom: 5,
              }}
            >
              <ShepTable
                tableTitle="Connections"
                isDataLoaded={recordLoaded}
                dataRows={currentRows}
                columnTitles={columns}
                selectionDisabled={true}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <SharedCatalogs
        open={openSharedCatalog}
        onClose={() => setOpenSharedCatalog(false)}
        rowData={selectedRow}
      />
    </>
  );
};
export default ConnectionPair;
